
import * as THREE from 'three';
import store from '../redux/store';
import {
  selected_object,
  setPushIntersectedObject,
  removeIntersectedObject,
} from '../redux/Slice/gridSlice';
import { selectObjectInScene } from '../pages/Dashboard/GridPage';
import {
  clearOutlineProxyForPoints,
  refreshParticleSelectionOutline,
} from './ThreeJsUtils/selectionOutline';
import {
  startAnimation,
  stopAnimation,
  pauseAnimation,
  syncPlaybackStateIfChanged,
} from './particles/animationControls';
import { setLifeTimeout, clearLifeTimeout, applyLifeTimeoutIfChanged } from './particles/lifeUtils';
import handleTerrain from './particles/handleTerrain';
import handleWater from './particles/handleWater';
import handleClouds from './particles/handleClouds';
import handleStars from './particles/handleStars';
import handleRain from './particles/handleRain';
import handleSnow from './particles/handleSnow';
import { ensurePointsMaterialOrthoSupport, resyncSceneParticlePoints } from './particles/syncParticlePointSizesForCamera';
import handleFoliage from './particles/handleFoliage';
import {
  createParticleTexture,
  applyParticleShapeMap,
  getDefaultParticleShape,
} from './particles/particleShapeTextures';
import { hasValidParticleCount } from './ThreeJsUtils/assetVisibilityUtils';

/** Vertical travel of the flame column (Range only changes base width). */
const FIRE_FLAME_HEIGHT = 2.6;
/** Typical visual lifetime of one spark (seconds) — short = continuous rising flow. */
const FIRE_PARTICLE_LIFESPAN_MIN = 0.45;
const FIRE_PARTICLE_LIFESPAN_MAX = 1.15;
/** Tip embers live a bit longer and drift off the flame tip. */
const FIRE_EMBER_LIFESPAN_MIN = 0.7;
const FIRE_EMBER_LIFESPAN_MAX = 1.6;
/** Default particle Size panel value (matches particle template). */
const FIRE_DEFAULT_SIZE = 1;
const FIRE_DEFAULT_RANGE = 0.35;
const FIRE_DEFAULT_CORE_COUNT = 220;
const FIRE_DEFAULT_TURBULENCE = 0.28;

const _fireColorHot = new THREE.Color(0xffffff);
const _fireColorMid = new THREE.Color(0xffb014);
const _fireColorCool = new THREE.Color(0xff3a00);
const _fireColorTmp = new THREE.Color();

function fireSpawnSpread(range) {
  return Math.max(0.06, Number(range) || FIRE_DEFAULT_RANGE);
}

/** Keep Size in panel range 0.1–5; do not remapping away from default 1. */
function clampFireSpriteSize(size) {
  const n = Number(size);
  if (!Number.isFinite(n) || n <= 0) return FIRE_DEFAULT_SIZE;
  return Math.max(0.1, Math.min(5, n));
}

function resetFireParticle(
  i,
  positions,
  velocities,
  colors,
  sizes,
  life,
  lifespans,
  config,
  asEmber = false
) {
  const index = i * 3;
  const spread = fireSpawnSpread(config.range);
  const s = config?.speedMultiplier ?? 1.0;
  const baseSize = clampFireSpriteSize(config.size);
  const sizeVariation = config.sizeVariation ?? 0.35;
  const flameHeight = config.fireHeight ?? FIRE_FLAME_HEIGHT;

  // ~18% tip sparks (like the reference licking embers)
  const isEmber = asEmber || Math.random() < 0.18;

  if (isEmber) {
    // Spawn near the upper third and lift away in a curved trail
    const angle = Math.random() * Math.PI * 2;
    const tipR = Math.pow(Math.random(), 0.7) * spread * 0.18;
    positions[index] = Math.cos(angle) * tipR;
    positions[index + 1] = flameHeight * (0.55 + Math.random() * 0.25);
    positions[index + 2] = Math.sin(angle) * tipR;

    velocities[index] = Math.cos(angle) * (0.15 + Math.random() * 0.45) * s;
    velocities[index + 1] = (0.55 + Math.random() * 0.9) * s;
    velocities[index + 2] = Math.sin(angle) * (0.15 + Math.random() * 0.45) * s;

    colors[index] = 1;
    colors[index + 1] = 0.55 + Math.random() * 0.25;
    colors[index + 2] = 0.12;

    const particleLife =
      FIRE_EMBER_LIFESPAN_MIN +
      Math.random() * (FIRE_EMBER_LIFESPAN_MAX - FIRE_EMBER_LIFESPAN_MIN);
    lifespans[i] = particleLife;
    life[i] = Math.random() * particleLife * 0.35;
    sizes[i] = baseSize * (0.18 + Math.random() * 0.22);
    return;
  }

  // Hot core: dense near center — teardrop base (Range = full base diameter)
  const angle = Math.random() * Math.PI * 2;
  const radius = Math.pow(Math.random(), 0.7) * (spread * 0.5);
  positions[index] = Math.cos(angle) * radius;
  positions[index + 1] = Math.random() * 0.06;
  positions[index + 2] = Math.sin(angle) * radius;

  // Strong upward buoyancy + slight outward draft (convection)
  const outward = 0.08 + Math.random() * 0.22;
  velocities[index] = Math.cos(angle) * outward * 0.28 * s;
  velocities[index + 1] = (1.35 + Math.random() * 1.55) * s;
  velocities[index + 2] = Math.sin(angle) * outward * 0.28 * s;

  // Hot white-yellow at spawn
  colors[index] = 1;
  colors[index + 1] = 0.97;
  colors[index + 2] = 0.78;

  const particleLife =
    FIRE_PARTICLE_LIFESPAN_MIN +
    Math.random() * (FIRE_PARTICLE_LIFESPAN_MAX - FIRE_PARTICLE_LIFESPAN_MIN);
  lifespans[i] = particleLife;
  // Stagger so the column looks continuous
  life[i] = Math.random() * particleLife;

  // Slightly larger near core center, smaller at rim
  const rim = radius / Math.max(1e-6, spread * 0.5);
  sizes[i] =
    baseSize * (0.7 + Math.random() * sizeVariation) * (1.05 - rim * 0.35);
}

function createFireGeometry(
  range = FIRE_DEFAULT_RANGE,
  fireHeight = FIRE_FLAME_HEIGHT,
  coreCount = FIRE_DEFAULT_CORE_COUNT,
  speedMultiplier = 1.0,
  size = FIRE_DEFAULT_SIZE,
  sizeVariation = 0.35
) {
  const positions = new Float32Array(coreCount * 3);
  const colors = new Float32Array(coreCount * 3);
  const sizes = new Float32Array(coreCount);
  const velocities = new Float32Array(coreCount * 3);
  const life = new Float32Array(coreCount);
  const lifespans = new Float32Array(coreCount);
  const baseSizes = new Float32Array(coreCount);

  const config = {
    range,
    speedMultiplier,
    size,
    sizeVariation,
    fireHeight,
  };

  for (let i = 0; i < coreCount; i++) {
    resetFireParticle(
      i,
      positions,
      velocities,
      colors,
      sizes,
      life,
      lifespans,
      config
    );
    baseSizes[i] = sizes[i];
  }

  const geometry = new THREE.BufferGeometry();
  geometry.setAttribute('position', new THREE.BufferAttribute(positions, 3));
  geometry.setAttribute('color', new THREE.BufferAttribute(colors, 3));
  // particleSize is consumed by the fire vertex-shader patch (not PointsMaterial.size)
  geometry.setAttribute(
    'particleSize',
    new THREE.BufferAttribute(sizes, 1)
  );
  geometry.userData = {
    velocities,
    life,
    lifespans,
    baseSizes,
    config,
  };

  return geometry;
}

/** Per-vertex size over life (standard PointsMaterial only has uniform size). */
function ensureFirePerParticleSize(material) {
  if (!material?.isPointsMaterial) return material;
  if (material.userData?.__fireSizeAttrPatched) return material;
  material.userData = material.userData || {};
  material.userData.__fireSizeAttrPatched = true;

  const previousCompile = material.onBeforeCompile;
  material.onBeforeCompile = (shader, renderer) => {
    if (typeof previousCompile === 'function') {
      previousCompile(shader, renderer);
    }
    if (!shader?.vertexShader?.includes('attribute float particleSize')) {
      shader.vertexShader = shader.vertexShader.replace(
        '#include <common>',
        '#include <common>\nattribute float particleSize;'
      );
      if (shader.vertexShader.includes('gl_PointSize = size;')) {
        shader.vertexShader = shader.vertexShader.replace(
          'gl_PointSize = size;',
          'gl_PointSize = max(particleSize, 0.001);'
        );
      }
    }
  };
  material.needsUpdate = true;
  return material;
}

const createFireMaterial = ({
  color = '#ffcc00',
  size = FIRE_DEFAULT_SIZE,
  opacity = 0.95,
  sizeAttenuation = true,
  blending = 'AdditiveBlending',
  shape = 'fire',
  shapeRotation = 0,
  shapeScale = 1,
} = {}) => {
  const shapeKey = String(shape || 'fire').toLowerCase();
  const textureSize = shapeKey === 'fire' ? 128 : 64;
  // White soft map — flame hues come from vertexColors × material.color
  const map = createParticleTexture(
    shape,
    '#ffffff',
    textureSize,
    shapeRotation,
    shapeScale
  );

  let blendingMode = THREE.AdditiveBlending;
  if (blending === 'NormalBlending') blendingMode = THREE.NormalBlending;
  else if (blending === 'MultiplyBlending') blendingMode = THREE.MultiplyBlending;
  else if (blending === 'AdditiveBlending') blendingMode = THREE.AdditiveBlending;
  // Fire shape always additive so it reads as glow, not opaque popcorn
  if (shapeKey === 'fire') blendingMode = THREE.AdditiveBlending;

  const pointsMaterial = new THREE.PointsMaterial({
    size,
    map,
    color: new THREE.Color(color),
    transparent: true,
    opacity: Math.min(1, opacity),
    depthWrite: false,
    alphaTest: 0.01,
    sizeAttenuation,
    vertexColors: true,
    blending: blendingMode,
  });

  pointsMaterial.setPerspective = function (fov, height) {
    this.userData = this.userData || {};
    this.userData.fov = fov;
    this.userData.height = height;
  };

  pointsMaterial.userData = pointsMaterial.userData || {};
  pointsMaterial.userData.baseSize = size;
  pointsMaterial.userData.authoredSize = size;
  pointsMaterial.userData.shape = shape;
  pointsMaterial.userData.shapeRotation = shapeRotation;
  pointsMaterial.userData.shapeScale = shapeScale;
  ensurePointsMaterialOrthoSupport(pointsMaterial);
  ensureFirePerParticleSize(pointsMaterial);

  pointsMaterial.update = function () {
    // Size over life is driven per-vertex in the animate loop.
  };

  return pointsMaterial;
};

/** Shared rising-flame integrator — used on create and kept across range edits. */
function createFireAnimateFunction(fire) {
  const clock = new THREE.Clock();

  const animateFire = () => {
    if (!fire.parent) {
      if (fire.userData?.animationId) {
        cancelAnimationFrame(fire.userData.animationId);
        fire.userData.animationId = null;
      }
      return;
    }

    const delta = Math.min(0.05, clock.getDelta());
    const geom = fire.geometry;
    if (!geom?.attributes?.position) {
      fire.userData.animationId = requestAnimationFrame(animateFire);
      return;
    }

    const posAttr = geom.getAttribute('position');
    const colorAttr = geom.getAttribute('color');
    const sizeAttr =
      geom.getAttribute('particleSize') || geom.getAttribute('size');
    const positions = posAttr.array;
    const colors = colorAttr?.array;
    const sizes = sizeAttr?.array;
    const ud = geom.userData || {};
    const speeds = ud.velocities;
    const ages = ud.life;
    const lives = ud.lifespans;
    const baseSizes = ud.baseSizes;
    const count = sizes ? sizes.length : positions.length / 3;
    const cfg = fire.userData || {};
    const geomSpeed = ud.config?.speedMultiplier ?? 1.0;
    const turb = Math.max(0, Number(cfg.turbulence) || 0.2);
    const flameHeight = cfg.fireHeight ?? FIRE_FLAME_HEIGHT;
    const spread = fireSpawnSpread(cfg.range);
    const wind = cfg.wind || { x: 0, y: 0, z: 0 };

    for (let i = 0; i < count; i++) {
      const idx3 = i * 3;
      const lifespan = Math.max(0.2, lives?.[i] || 1);
      let age = ages[i] + delta * geomSpeed;
      ages[i] = age;
      const progress = Math.min(1, age / lifespan);

      // Integrate velocity (units / second)
      positions[idx3] += speeds[idx3] * delta * geomSpeed;
      positions[idx3 + 1] += speeds[idx3 + 1] * delta * geomSpeed;
      positions[idx3 + 2] += speeds[idx3 + 2] * delta * geomSpeed;

      // Buoyancy + flicker: turbulence grows as the spark rises
      const heightRatio = Math.min(
        1,
        Math.max(0, positions[idx3 + 1] / flameHeight)
      );
      const swirl =
        Math.sin(age * 16 + i * 0.7) *
        (0.018 + turb * 0.1) *
        (0.35 + heightRatio * 1.2);
      const flicker =
        Math.cos(age * 23 + i) *
        (0.014 + turb * 0.07) *
        (0.4 + heightRatio * 1.1);
      positions[idx3] += swirl + (Math.random() - 0.5) * 0.014 * turb;
      positions[idx3 + 2] += flicker + (Math.random() - 0.5) * 0.014 * turb;

      // Teardrop: pull hard toward center as sparks rise (narrow tip)
      const taperPull = 0.55 + heightRatio * 1.4;
      positions[idx3] *= 1 - taperPull * delta;
      positions[idx3 + 2] *= 1 - taperPull * delta;

      // Slow horizontal drift; accelerate upward (heat plume)
      speeds[idx3] *= 1 - 0.55 * delta;
      speeds[idx3 + 2] *= 1 - 0.55 * delta;
      speeds[idx3 + 1] += 0.35 * delta * geomSpeed;

      // Size: fat near base, shrinks / flickers out near tip
      if (sizes && baseSizes) {
        const grow = progress < 0.2 ? 0.85 + progress * 1.1 : 1.07 - (progress - 0.2) * 1.05;
        const pulse = 0.9 + 0.1 * Math.sin(age * 32 + i);
        sizes[i] = Math.max(0.015, baseSizes[i] * grow * pulse);
      }

      // Color over life: white-hot → yellow → orange-red (× material.color)
      if (colors) {
        if (progress < 0.28) {
          _fireColorTmp.copy(_fireColorHot).lerp(_fireColorMid, progress / 0.28);
        } else {
          _fireColorTmp
            .copy(_fireColorMid)
            .lerp(_fireColorCool, (progress - 0.28) / 0.72);
        }
        const fade = 1 - progress * progress * 0.92;
        colors[idx3] = _fireColorTmp.r * fade;
        colors[idx3 + 1] = _fireColorTmp.g * fade;
        colors[idx3 + 2] = _fireColorTmp.b * fade;
      }

      const expired =
        progress >= 1 ||
        positions[idx3 + 1] > flameHeight * (0.88 + Math.random() * 0.18);
      if (expired) {
        resetFireParticle(
          i,
          positions,
          speeds,
          colors || new Float32Array(3),
          sizes || new Float32Array(1),
          ages,
          lives,
          {
            range: spread,
            speedMultiplier: geomSpeed,
            size: clampFireSpriteSize(cfg.size),
            sizeVariation: cfg.sizeVariation ?? 0.35,
            fireHeight: flameHeight,
          }
        );
        if (baseSizes && sizes) baseSizes[i] = sizes[i];
        ages[i] = Math.random() * 0.12;
      }
    }

    posAttr.needsUpdate = true;
    if (colorAttr) colorAttr.needsUpdate = true;
    if (sizeAttr) sizeAttr.needsUpdate = true;

    fire.position.x += (wind.x || 0) * delta;
    fire.position.z += (wind.z || 0) * delta;

    fire.userData.animationId = requestAnimationFrame(animateFire);
  };

  return animateFire;
}

const handleFire = (scene, particle) => {
  if (!scene || typeof camera === 'undefined') return;

  const rawCore = particle?.coreCount;
  const coreCount =
    rawCore === 0 || rawCore === '0' || rawCore === ''
      ? 0
      : parseInt(rawCore) || FIRE_DEFAULT_CORE_COUNT;
  const emberCount = particle?.emberCount || 150;
  const color = particle.color || '#ffcc00';
  const opacity = particle?.opacity ?? 0.95;
  const size = clampFireSpriteSize(particle?.size);
  const range = particle?.range ?? FIRE_DEFAULT_RANGE;
  const turbulence =
    particle?.turbulence !== undefined
      ? particle.turbulence
      : FIRE_DEFAULT_TURBULENCE;
  const life = particle?.life ?? 120;
  const sizeVariation =
    particle?.sizeVariation !== undefined ? particle.sizeVariation : 0.35;
  const velocity = particle?.velocity || { x: 0, y: 0, z: 0 };
  const wind = particle?.wind || { x: 0, y: 0, z: 0 };
  const heatIntensity = particle?.heatIntensity || 0.1;
  const blending = particle?.blending || 'AdditiveBlending';
  const sizeAttenuation =
    particle?.sizeAttenuation !== undefined ? particle.sizeAttenuation : true;
  const shape = particle?.shape ?? getDefaultParticleShape('fire');
  const shapeRotation = particle?.shapeRotation || 0;
  const shapeScale = particle?.shapeScale || 1.0;

  const fireHeight = FIRE_FLAME_HEIGHT;
  const speedMultiplier = particle?.speedMultiplier ?? 1.0;

  const geometry = createFireGeometry(
    range,
    fireHeight,
    coreCount,
    speedMultiplier,
    size,
    sizeVariation
  );
  const material = createFireMaterial({
    color,
    size,
    opacity,
    sizeAttenuation,
    blending,
    shape,
    shapeRotation,
    shapeScale,
  });
  material.setPerspective(camera.fov, window.innerHeight);

  const fire = new THREE.Points(geometry, material);

  const uniqueId = `fire_${Date.now()}`;
  fire.name = particle?.id || particle?._id || uniqueId;
  fire.url = uniqueId;
  fire.asset_file_name = 'fire';
  fire.file_name = 'fire';
  fire.mesh_code = 'fire';
  fire.glb_type = 'shapes';
  fire.deleteType = 'particle';
  fire.objType = 'fire';
  fire.createdAt = new Date().toISOString();

  fire.userData = {
    material_settings: {
      shader: 'Standard',
      albedo: color,
      roughness: 0.0,
      metalness: 0.0,
    },
    coreCount,
    emberCount,
    color,
    opacity,
    size,
    range,
    fireHeight,
    turbulence,
    life,
    sizeVariation,
    velocity,
    wind,
    heatIntensity,
    blending,
    sizeAttenuation,
    shape,
    shapeRotation,
    shapeScale,
    speedMultiplier,
    __fireSimVersion: 2,
  };

  if (particle?.position) {
    fire.position.set(
      particle.position.x || 0,
      particle.position.y || 0,
      particle.position.z || 0
    );
  }
  if (particle?.rotation) {
    fire.rotation.set(
      particle.rotation.x || 0,
      particle.rotation.y || 0,
      particle.rotation.z || 0
    );
  }
  if (particle?.scale) {
    fire.scale.set(
      particle.scale.x || 1,
      particle.scale.y || 1,
      particle.scale.z || 1
    );
  }

  scene.add(fire);
  resyncSceneParticlePoints(scene);
  requestAnimationFrame(() => {
    store.dispatch(setPushIntersectedObject(fire));
    store.dispatch(selected_object(fire));
    selectObjectInScene(fire.name);
  });

  const animateFire = createFireAnimateFunction(fire);
  fire.userData.animateFunction = animateFire;

  if (!particle?.isStopped) {
    startAnimation(fire, animateFire, fire.name);
    setLifeTimeout(
      fire,
      typeof life === 'number' ? life : parseFloat(life) || 0
    );
  } else {
    clearLifeTimeout(fire);
  }
};

const updateFire = (scene, fireSystem, newParticleData) => {
  if (!scene || !fireSystem || fireSystem.objType !== 'fire') {
    console.warn('Invalid fire system or scene provided for update');
    return;
  }

  // Get current configuration
  const currentConfig = fireSystem.userData || {};

  // Play/pause/stop only when those flags actually change.
  syncPlaybackStateIfChanged(fireSystem, newParticleData);

  // Merge current config with new particle data
  const updatedConfig = {
    coreCount:
      newParticleData?.coreCount ??
      currentConfig.coreCount ??
      FIRE_DEFAULT_CORE_COUNT,
    emberCount: newParticleData?.emberCount ?? currentConfig.emberCount ?? 150,
    color: newParticleData?.color ?? currentConfig.color ?? '#ffcc00',
    opacity: newParticleData?.opacity ?? currentConfig.opacity ?? 0.95,
    size: clampFireSpriteSize(
      newParticleData?.size ?? currentConfig.size ?? FIRE_DEFAULT_SIZE
    ),
    range:
      newParticleData?.range ?? currentConfig.range ?? FIRE_DEFAULT_RANGE,
    fireHeight: currentConfig.fireHeight ?? FIRE_FLAME_HEIGHT,
    turbulence:
      newParticleData?.turbulence ??
      currentConfig.turbulence ??
      FIRE_DEFAULT_TURBULENCE,
    life: newParticleData?.life ?? currentConfig.life ?? 120,
    sizeVariation:
      newParticleData?.sizeVariation ?? currentConfig.sizeVariation ?? 0.35,
    velocity: newParticleData?.velocity ??
      currentConfig.velocity ?? { x: 0, y: 0, z: 0 },
    wind: newParticleData?.wind ?? currentConfig.wind ?? { x: 0, y: 0, z: 0 },
    heatIntensity:
      newParticleData?.heatIntensity ?? currentConfig.heatIntensity ?? 0.1,
    blending:
      newParticleData?.blending ??
      currentConfig.blending ??
      'AdditiveBlending',
    sizeAttenuation:
      newParticleData?.sizeAttenuation !== undefined
        ? newParticleData.sizeAttenuation
        : currentConfig.sizeAttenuation !== undefined
          ? currentConfig.sizeAttenuation
          : true,
    shape:
      newParticleData?.shape ??
      currentConfig.shape ??
      getDefaultParticleShape('fire'),
    shapeRotation:
      newParticleData?.shapeRotation ?? currentConfig.shapeRotation ?? 0,
    shapeScale: newParticleData?.shapeScale ?? currentConfig.shapeScale ?? 1.0,
    speedMultiplier:
      newParticleData?.speedMultiplier ?? currentConfig.speedMultiplier ?? 1.0,
  };
// Handle in-place geometry update for coreCount changes to preserve selection and focus
const isCoreCountProvided = newParticleData?.coreCount !== undefined;
const targetCoreCount = isCoreCountProvided
  ? newParticleData.coreCount === '' || newParticleData.coreCount === null
    ? 0
    : Math.max(0, parseInt(newParticleData.coreCount) || 0)
  : currentConfig.coreCount;

const coreCountChanged =
  isCoreCountProvided && targetCoreCount !== currentConfig.coreCount;

if (coreCountChanged) {
  if (fireSystem.geometry) {
    fireSystem.geometry.dispose();
  }
  const fireHeight =
    fireSystem.userData?.fireHeight ?? FIRE_FLAME_HEIGHT;
  const newGeometry = createFireGeometry(
    updatedConfig.range ?? currentConfig.range ?? FIRE_DEFAULT_RANGE,
    fireHeight,
    targetCoreCount,
    updatedConfig.speedMultiplier,
    updatedConfig.size,
    updatedConfig.sizeVariation
  );
  newGeometry.computeBoundingBox();
  newGeometry.computeBoundingSphere();
  fireSystem.geometry = newGeometry;
  fireSystem.userData.coreCount = targetCoreCount;
  fireSystem.userData.fireHeight = fireHeight;
  currentConfig.coreCount = targetCoreCount;
  refreshParticleSelectionOutline();
}

// Range = horizontal width only — scale X/Z spread in place; keep flame height fixed.
const prevRange = currentConfig.range ?? 0.5;
const nextRange = updatedConfig.range ?? prevRange;
const rangeChanged =
  newParticleData?.range !== undefined &&
  Number.isFinite(Number(nextRange)) &&
  Math.abs(Number(nextRange) - Number(prevRange)) > 1e-6;

if (rangeChanged && fireSystem.geometry?.attributes?.position) {
  const positions = fireSystem.geometry.attributes.position.array;
  const safePrev = Math.abs(Number(prevRange)) < 1e-6 ? 1e-6 : Number(prevRange);
  const rangeRatio = Number(nextRange) / safePrev;

  for (let i = 0; i < positions.length; i += 3) {
    positions[i] *= rangeRatio;
    positions[i + 2] *= rangeRatio;
    // Y (height) unchanged — Range is width/spread only.
  }
  fireSystem.geometry.attributes.position.needsUpdate = true;
  fireSystem.geometry.computeBoundingBox();
  fireSystem.geometry.computeBoundingSphere();

  if (fireSystem.geometry.userData?.config) {
    fireSystem.geometry.userData.config.range = Number(nextRange);
  }
  fireSystem.userData.range = Number(nextRange);
  if (fireSystem.userData.fireHeight == null) {
    fireSystem.userData.fireHeight = FIRE_FLAME_HEIGHT;
  }
  currentConfig.range = Number(nextRange);
  fireSystem.userData.forceBoxUpdate = true;
}

  // For non-recreation updates, update the material properties
  if (fireSystem.material) {
    // Update color if changed
    if (newParticleData?.color !== undefined) {
      fireSystem.material.color.set(updatedConfig.color);
    }

    // Update opacity if changed
    if (newParticleData?.opacity !== undefined) {
      fireSystem.material.opacity = updatedConfig.opacity;
      fireSystem.material.transparent = true;
    }

    // Update size if changed
    if (newParticleData?.size !== undefined) {
      const nextSize = clampFireSpriteSize(newParticleData.size);
      const prevSize = clampFireSpriteSize(
        currentConfig.size ?? FIRE_DEFAULT_SIZE
      );
      fireSystem.material.size = nextSize;
      fireSystem.material.userData.baseSize = nextSize;
      fireSystem.material.userData.authoredSize = nextSize;
      ensureFirePerParticleSize(fireSystem.material);

      const geom = fireSystem.geometry;
      if (geom && !geom.getAttribute('particleSize')) {
        const legacySize = geom.getAttribute('size');
        if (legacySize) {
          geom.setAttribute('particleSize', legacySize.clone());
        }
      }
      const sizeAttr =
        geom?.getAttribute('particleSize') || geom?.getAttribute('size');
      const baseSizes = geom?.userData?.baseSizes;

      if (sizeAttr && Math.abs(nextSize - prevSize) > 1e-6) {
        const ratio = nextSize / Math.max(prevSize, 1e-6);
        const arr = sizeAttr.array;
        for (let i = 0; i < arr.length; i++) {
          arr[i] *= ratio;
          if (baseSizes && i < baseSizes.length) {
            baseSizes[i] *= ratio;
          }
        }
        sizeAttr.needsUpdate = true;
      } else if (sizeAttr && (!baseSizes || baseSizes.length !== sizeAttr.count)) {
        // No baseSizes (legacy) — rebuild so Size drives visible sprites
        const arr = sizeAttr.array;
        const rebuilt = new Float32Array(arr.length);
        for (let i = 0; i < arr.length; i++) {
          rebuilt[i] = nextSize * (0.55 + Math.random() * 0.5);
          arr[i] = rebuilt[i];
        }
        geom.userData.baseSizes = rebuilt;
        sizeAttr.needsUpdate = true;
      }

      if (geom?.userData?.config) {
        geom.userData.config.size = nextSize;
      }
      currentConfig.size = nextSize;
      updatedConfig.size = nextSize;
    }

    fireSystem.material.needsUpdate = true;
  }

  // Handle shape / texture updates without full recreation
  if (
    fireSystem.material &&
    (newParticleData?.shape ||
      newParticleData?.color !== undefined ||
      newParticleData?.shapeRotation !== undefined ||
      newParticleData?.shapeScale !== undefined)
  ) {
    const newShape =
      newParticleData?.shape ??
      fireSystem.userData?.shape ??
      updatedConfig.shape;
    const newRotation =
      newParticleData?.shapeRotation ??
      fireSystem.userData?.shapeRotation ??
      updatedConfig.shapeRotation;
    const newScale =
      newParticleData?.shapeScale ??
      fireSystem.userData?.shapeScale ??
      updatedConfig.shapeScale;

    // Only update if different from existing
    const oldShape = fireSystem.material.userData?.shape;
    const oldRotation = fireSystem.material.userData?.shapeRotation;
    const oldScale = fireSystem.material.userData?.shapeScale;

    const shapeChanged =
      newShape !== oldShape ||
      newRotation !== oldRotation ||
      newScale !== oldScale ||
      newParticleData?.color !== undefined;

    if (shapeChanged) {
      try {
        const textureSize =
          String(newShape).toLowerCase() === 'fire' ? 128 : 64;
        applyParticleShapeMap(
          fireSystem.material,
          newShape,
          updatedConfig.color,
          newRotation,
          newScale,
          textureSize
        );
        fireSystem.material.userData = fireSystem.material.userData || {};
        fireSystem.material.userData.shape = newShape;
        fireSystem.material.userData.shapeRotation = newRotation;
        fireSystem.material.userData.shapeScale = newScale;
        // Keep vertexColors so white→orange→red over life still works
        fireSystem.material.vertexColors = true;
        // Fire must glow; applyParticleShapeMap defaults to Normal for other particles
        if (String(newShape).toLowerCase() === 'fire') {
          fireSystem.material.blending = THREE.AdditiveBlending;
        }
        ensurePointsMaterialOrthoSupport(fireSystem.material);
        ensureFirePerParticleSize(fireSystem.material);
        fireSystem.material.needsUpdate = true;
      } catch (err) {
        console.warn('Failed to update particle shape texture', err);
      }
    }
  }

  // Update transforms only when Redux intentionally differs from the live
  // object. Property-panel dumps often keep stale identity transforms and
  // would snap a gizmo-moved fire back to the scene center.
  const applyFireTransformIfNeeded = (target, next, kind = 'position') => {
    if (!next || !target) return;
    const cur = { x: target.x, y: target.y, z: target.z };
    const resolved = {
      x: Number.isFinite(Number(next.x)) ? Number(next.x) : cur.x,
      y: Number.isFinite(Number(next.y)) ? Number(next.y) : cur.y,
      z: Number.isFinite(Number(next.z)) ? Number(next.z) : cur.z,
    };

    let nextIsIdentity = false;
    let liveIsCustom = false;
    if (kind === 'scale') {
      nextIsIdentity =
        Math.abs(resolved.x - 1) < 1e-8 &&
        Math.abs(resolved.y - 1) < 1e-8 &&
        Math.abs(resolved.z - 1) < 1e-8;
      liveIsCustom =
        Math.abs(cur.x - 1) > 1e-4 ||
        Math.abs(cur.y - 1) > 1e-4 ||
        Math.abs(cur.z - 1) > 1e-4;
    } else {
      // position / rotation default identity is 0
      nextIsIdentity =
        Math.abs(resolved.x) < 1e-8 &&
        Math.abs(resolved.y) < 1e-8 &&
        Math.abs(resolved.z) < 1e-8;
      liveIsCustom =
        Math.abs(cur.x) > 1e-4 ||
        Math.abs(cur.y) > 1e-4 ||
        Math.abs(cur.z) > 1e-4;
    }
    if (nextIsIdentity && liveIsCustom) return;

    const changed =
      Math.abs(resolved.x - cur.x) > 1e-5 ||
      Math.abs(resolved.y - cur.y) > 1e-5 ||
      Math.abs(resolved.z - cur.z) > 1e-5;
    if (!changed) return;
    target.set(resolved.x, resolved.y, resolved.z);
  };

  applyFireTransformIfNeeded(
    fireSystem.position,
    newParticleData?.position,
    'position'
  );
  applyFireTransformIfNeeded(
    fireSystem.rotation,
    newParticleData?.rotation,
    'rotation'
  );
  applyFireTransformIfNeeded(fireSystem.scale, newParticleData?.scale, 'scale');

  // Update userData with new configuration
  fireSystem.userData = {
    ...fireSystem.userData,
    ...updatedConfig,
    material_settings: {
      ...fireSystem.userData.material_settings,
      albedo: updatedConfig.color,
    },
  };

  // Keep / refresh rising-flame integrator so older fires pick up motion fixes
  const needsAnimateRefresh =
    !fireSystem.userData.animateFunction ||
    fireSystem.userData.__fireSimVersion !== 2 ||
    coreCountChanged;
  if (needsAnimateRefresh) {
    const geom = fireSystem.geometry;
    if (geom && !geom.getAttribute('particleSize')) {
      const legacySize = geom.getAttribute('size');
      if (legacySize) {
        geom.setAttribute('particleSize', legacySize.clone());
      }
    }
    if (fireSystem.material) {
      ensurePointsMaterialOrthoSupport(fireSystem.material);
      ensureFirePerParticleSize(fireSystem.material);
      fireSystem.material.vertexColors = true;
      fireSystem.material.blending = THREE.AdditiveBlending;
      fireSystem.material.needsUpdate = true;
    }
    const wasPlaying =
      fireSystem.userData.isPlaying !== false &&
      fireSystem.userData.isStopped !== true;
    if (fireSystem.userData.animationId) {
      stopAnimation(fireSystem, fireSystem.name);
    }
    const animateFire = createFireAnimateFunction(fireSystem);
    fireSystem.userData.animateFunction = animateFire;
    fireSystem.userData.__fireSimVersion = 2;
    if (wasPlaying) {
      startAnimation(fireSystem, animateFire, fireSystem.name);
    }
  }

  // Update system metadata if ID changed
  if (newParticleData?.id && newParticleData.id !== fireSystem.name) {
    fireSystem.name = newParticleData.id;
    fireSystem.url = newParticleData.id;
  }
// Update selections and transform controls (only if not already selected to avoid closing open UI popups)
const currentSelected = store.getState().assets_object?.select_object;
if (currentSelected !== fireSystem && currentSelected?.name !== fireSystem.name) {
  store.dispatch(setPushIntersectedObject(fireSystem));
  store.dispatch(selected_object(fireSystem));
  selectObjectInScene(fireSystem.name);
}


  // Manage life timeout only when Life(sec) actually changes
  applyLifeTimeoutIfChanged(
    fireSystem,
    newParticleData?.life,
    currentConfig.life
  );

  resyncSceneParticlePoints(scene);
  return fireSystem;
};

// Helper function to find fire system by name/id in scene
const findFireSystem = (scene, nameOrId) => {
  return scene.children.find(
    (child) =>
      (child.name === nameOrId || child.url === nameOrId) &&
      child.objType === 'fire'
  );
};

// Convenience function to update fire by name
const updateFireByName = (scene, nameOrId, newParticleData) => {
  const fireSystem = findFireSystem(scene, nameOrId);
  if (fireSystem) {
    return updateFire(scene, fireSystem, newParticleData);
  } else {
    console.warn(`Fire system with name/id "${nameOrId}" not found in scene`);
    return null;
  }
};

export {
  handleTerrain,
  handleWater,
  handleClouds,
  handleStars,
  handleRain,
  handleSnow,
  handleFire,
  handleFoliage,
  updateFire,
  updateFireByName,
};
